/* eslint-disable react/prop-types */
import React, { FunctionComponent, ChangeEvent } from "react";
import PropTypes from "prop-types";
import Theme from "../../../theme/theme";
import { StyledLabel, StyledSelect, Required } from "./styles";

export interface SelectProps {
  name: string;
  label?: string;
  id: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  maxWidth?: number;
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

/**
  `Select` element represents a control that provides a menu of options.
  <br />
  Use `<Field />` component as a wrapper.
  <br />
  The `<select>` element has some unique attributes you can use to control it, such as `multiple` to specify whether multiple options can be selected, and `size` to specify how many options should be shown at once.
  <br />
  It also accepts most of the general form input attributes such as `required`, `disabled`, `autofocus`, etc.
*/

export const Select: FunctionComponent<SelectProps> = ({
  children,
  name,
  label,
  value,
  id,
  disabled,
  onChange,
  maxWidth,
  required,
}) => (
  <Theme>
    {label && (
      <StyledLabel htmlFor={name} disabled={disabled}>
        {label}
        {required && <Required disabled={disabled} />}
      </StyledLabel>
    )}
    <StyledSelect
      name={name}
      value={value}
      data-testid={id}
      maxWidth={maxWidth}
      aria-label={label}
      disabled={disabled}
      onChange={onChange}
      id={id}
    >
      {children}
    </StyledSelect>
  </Theme>
);

Select.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  maxWidth: PropTypes.number,
};

Select.defaultProps = {
  label: null,
  value: null,
  disabled: null,
  required: null,
  maxWidth: null,
};

export default Select;
